import React, { useState, useEffect } from "react";
import { Copy, Check, Users, Gift, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { base44 as db } from "@/api/base44Client";
import useCurrentUser from "@/lib/useCurrentUser";

const REFERRAL_TIERS = [
  { friends: 1, reward: 5, color: "bg-blue-100 border-blue-300 dark:bg-blue-900 dark:border-blue-700" },
  { friends: 2, reward: 10, color: "bg-purple-100 border-purple-300 dark:bg-purple-900 dark:border-purple-700" },
  { friends: 3, reward: 15, color: "bg-green-100 border-green-300 dark:bg-green-900 dark:border-green-700" },
];

export default function ReferralPanel() {
  const { user } = useCurrentUser();
  const [copied, setCopied] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      fetchReferralData();
    }
  }, [user?.user_id]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      try {
        const data = await db.auth.getReferralData?.();
        if (data) {
          setReferralData(data);
          console.log("Referral data loaded:", data);
        }
      } catch (e) {
        console.log("getReferralData failed, using fallback:", e.message);
        throw e;
      }
    } catch (e) {
      const fallbackData = {
        referral_code: user.user_id.substring(0, 8).toUpperCase(),
        invited_friends: [],
        total_friends: 0,
        pending_friends: 0,
      };
      setReferralData(fallbackData);
      console.log("Using fallback referral data:", fallbackData);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = referralData 
    ? `${window.location.origin}/register?ref=${referralData.referral_code}`
    : "";

  const totalFriends = referralData?.total_friends || 0;
  const currentReward = REFERRAL_TIERS.find(t => totalFriends >= t.friends)?.reward || 0;
  const nextReward = REFERRAL_TIERS.find(t => totalFriends < t.friends);

  const copyLink = () => {
    if (!referralLink) {
      toast.error("Link wird noch geladen...");
      return;
    }
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral-Link kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWhatsapp = () => {
    if (!referralLink) {
      toast.error("Link wird noch geladen...");
      return;
    }
    const message = encodeURIComponent(
      `Ich lerne mit LAP QUIZ! 🎓 Melde dich mit meinem Link an und wir beide bekommen Belohnungen:\n${referralLink}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const shareViaMail = () => {
    if (!referralLink) {
      toast.error("Link wird noch geladen...");
      return;
    }
    const subject = encodeURIComponent("Lerne mit LAP QUIZ!");
    const body = encodeURIComponent(
      `Hallo,\n\nIch lerne mit LAP QUIZ zur LAP-Vorbereitung. Komm auch dazu und melde dich mit meinem Link an:\n\n${referralLink}\n\nWir beide bekommen Belohnungen!\n\nViele Grüße`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const redeemReward = async () => {
    if (currentReward === 0) {
      toast.error("Du brauchst mind. 1 Freund");
      return;
    }
    setRedeeming(true);
    try {
      await db.auth.redeemReferralReward?.({ amount: currentReward });
      toast.success(`${currentReward} CHF TWINT werden überwiesen!`);
      await fetchReferralData();
    } catch (e) {
      toast.error("Einlösen fehlgeschlagen");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="space-y-4 mt-4 not-italic">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-black dark:text-yellow-300 not-italic">Freunde einladen</p>
        <h2 className="font-heading text-lg font-extrabold text-black dark:text-white not-italic">Verdiene mit LAP QUIZ</h2>
      </div>

      {/* Reward Tiers */}
      <div className="grid grid-cols-3 gap-2">
        {REFERRAL_TIERS.map((tier) => (
          <div
            key={tier.friends}
            className={`brutal border-2 border-black dark:border-white rounded-lg p-3 text-center transition-all not-italic ${
              totalFriends >= tier.friends ? tier.color : "bg-gray-100 dark:bg-gray-800 opacity-50"
            }`}
          >
            <div className="text-2xl font-bold text-black dark:text-white not-italic">{tier.reward}</div>
            <div className="text-[10px] font-bold uppercase text-black dark:text-yellow-300 not-italic">CHF TWINT</div>
            <div className="text-[9px] text-black dark:text-gray-100 mt-1 not-italic" style={{color: 'inherit'}}>{tier.friends} Freund{tier.friends > 1 ? "e" : ""}</div>
          </div>
        ))}
      </div>

      {/* Status */}
      <div className="brutal bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-3 not-italic">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-black dark:text-white" />
          <span className="font-bold text-sm text-black dark:text-white not-italic">Dein Status</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase text-black dark:text-gray-300 not-italic" style={{color: 'inherit'}}>Eingeladene</p>
            <p className="text-xl font-bold text-black dark:text-white not-italic">{totalFriends}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-black dark:text-gray-300 not-italic" style={{color: 'inherit'}}>Verdient</p>
            <p className="text-xl font-bold text-black dark:text-white not-italic">{currentReward} CHF</p>
          </div>
        </div>
      </div>

      {/* Next Reward */}
      {nextReward && (
        <div className="brutal bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-3 not-italic">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            <span className="font-bold text-sm text-amber-900 dark:text-amber-300 not-italic">Nächste Belohnung</span>
          </div>
          <p className="text-sm text-amber-900 dark:text-amber-300 font-semibold not-italic">
            {nextReward.friends - totalFriends} weitere Freund{nextReward.friends - totalFriends > 1 ? "e" : ""} → {nextReward.reward} CHF TWINT
          </p>
        </div>
      )}

      {/* Referral Link */}
      <div className="brutal bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-3 not-italic">
        <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900 dark:text-blue-300 mb-2 not-italic">Dein Einladungs-Link</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="flex-1 bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 rounded px-2 py-1.5 text-xs font-mono text-black dark:text-white truncate not-italic"
            placeholder="Link wird geladen..."
          />
          <button
            onClick={copyLink}
            disabled={!referralLink}
            className="brutal-btn flex items-center gap-1 bg-blue-600 dark:bg-blue-700 text-white border-2 border-blue-700 dark:border-blue-600 rounded px-3 py-1.5 hover:bg-blue-700 dark:hover:bg-blue-600 transition-all font-bold text-xs disabled:opacity-50 not-italic"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Kopiert" : "Kopieren"}
          </button>
        </div>
      </div>

      {/* Share Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={shareViaWhatsapp}
          disabled={!referralLink}
          className="brutal-btn bg-green-500 dark:bg-green-700 text-white border-2 border-green-700 dark:border-green-600 rounded-lg py-2 font-bold text-sm hover:bg-green-600 dark:hover:bg-green-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 not-italic"
        >
          <Share2 className="w-4 h-4" />
          WhatsApp
        </button>
        <button
          onClick={shareViaMail}
          disabled={!referralLink}
          className="brutal-btn bg-gray-600 dark:bg-gray-700 text-white border-2 border-gray-700 dark:border-gray-600 rounded-lg py-2 font-bold text-sm hover:bg-gray-700 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 not-italic"
        >
          <Share2 className="w-4 h-4" />
          Email
        </button>
      </div>

      {/* Redeem Button */}
      {currentReward > 0 && (
        <button
          onClick={redeemReward}
          disabled={redeeming || currentReward === 0}
          className="brutal-btn bg-black dark:bg-white text-white dark:text-black w-full py-3 font-bold rounded-lg hover:bg-gray-900 dark:hover:bg-gray-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2 not-italic"
        >
          {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
          {redeeming ? "Wird eingel…" : `${currentReward} CHF TWINT einlösen`}
        </button>
      )}

      {/* Info */}
      <div className="brutal bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700 rounded-lg p-2.5 text-[11px] text-black dark:text-gray-200 leading-snug not-italic">
        <p className="font-bold mb-1 text-black dark:text-white not-italic">💡 Wie funktioniert es?</p>
        <ul className="space-y-1 list-disc list-inside text-black dark:text-gray-200 not-italic">
          <li className="not-italic">Teile deinen Link mit Freunden</li>
          <li className="not-italic">Wenn sie sich anmelden, zählen sie zu deinen Einladungen</li>
          <li className="not-italic">Ab 1 Freund: 5 CHF TWINT, ab 2: 10 CHF, ab 3: 15 CHF</li>
          <li className="not-italic">Belohnungen werden via TWINT ausbezahlt</li>
        </ul>
      </div>
    </div>
  );
}
